/* eslint-disable no-magic-numbers */
import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import DOMPurify from 'dompurify';

import { useReportBuilderContext } from '../../report-builder.context';
import { CARD_TYPES } from '../../types';
import { textCardStyles } from './text-card.styles';
import { parseReportText } from '../../utils-dates';
import { areHTMLStringsEqual } from '../../utils-generic';
import { textWidgetAllowedTags } from '../../constants';
import { FormattedMessage, useIntl } from 'react-intl';

export const textEditorFontSizeOptions = [
  '8px',
  '9px',
  '10px',
  '11px',
  '12px',
  '14px',
  '18px',
  '24px',
  '30px',
  '36px',
  '48px',
  '60px',
  '72px',
  '96px'
];
const Font = Quill.import('attributors/class/font') as { whitelist: string[] };
Font.whitelist = ['SageUI', 'Arial', 'times-new-roman', 'serif', 'monospace'];
Quill.register(Font as never, true);
const Size = Quill.import('attributors/style/size') as { whitelist: string[] };
Size.whitelist = textEditorFontSizeOptions;
Quill.register(Size as never, true);

export const TextCard = ({ html, cardId }: { html: string; cardId: string }) => {
  const intl = useIntl();
  const { currentCard, updateTextData, userTimezone, isOnboarding } = useReportBuilderContext();
  const [value, setValue] = useState(currentCard ? html : parseReportText(html, userTimezone));
  const [modules, setModules] = useState<Record<string, Record<string, string>>>({});
  const classes = textCardStyles();
  const editorRef = useRef<ReactQuill>(null);
  const focusIntervalRef = useRef<number>();

  const handleOnChange = (newValue: string) => {
    setValue(newValue);
    // we can't just compare strings because sanitization can sometimes swap the order of attributes or styles
    if (!areHTMLStringsEqual(currentCard?.[CARD_TYPES.TEXT]?.html || '', newValue)) {
      updateTextData({ html: newValue });
    }
  };

  useEffect(() => {
    if (currentCard?.i && currentCard?.i === cardId) {
      setModules({
        toolbar: {
          container: `#toolbar-${currentCard?.i}`
        }
      });
      setValue(currentCard[CARD_TYPES.TEXT]?.html || '');
      focusIntervalRef.current = (setInterval(() => {
        if (editorRef.current) {
          const quill = editorRef.current.getEditor();
          quill.setSelection(quill.getLength(), 0);
          clearInterval(focusIntervalRef.current);
        }
      }, 50) as unknown) as number;
    } else {
      setModules({});
      setValue(parseReportText(html, userTimezone));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCard?.i, cardId]);

  const handleOnClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const cleanText = value.replace(/<\/?[^>]+(>|$)/g, '');

  return (
    <div className={classes.root}>
      {modules.toolbar && !isOnboarding && currentCard?.i === cardId ? (
        <div onMouseDown={handleOnClick}>
          <ReactQuill
            theme="snow"
            // sanitization can break react quill, should be done when first loading the report
            // Quill handles sanitization well on its own if any code is inserted inside the editor
            value={value}
            onChange={handleOnChange}
            modules={modules}
            // Quill 2 / react-quill-new default `useSemanticHTML` is true, which strips Quill's
            // presentational classes (e.g. `ql-align-*`). That made formatting-only edits like
            // text alignment never trigger onChange, so changes weren't persisted. Disable it so
            // we receive the full HTML (with classes) on every change.
            useSemanticHTML={false}
            placeholder={intl.formatMessage({
              id: 'common.start_typing_here',
              defaultMessage: 'Start typing here ...'
            })}
            ref={editorRef}
          />
        </div>
      ) : (
        <div className="ql-snow ql-container">
          {cleanText ? (
            <div
              className="ql-editor"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(value, {
                  ALLOWED_TAGS: textWidgetAllowedTags,
                  ALLOWED_ATTR: ['style', 'src', 'class']
                })
              }}
            />
          ) : (
            <FormattedMessage id="common.start_typing_here" defaultMessage="Start typing here ..." />
          )}
        </div>
      )}
    </div>
  );
};
